import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Icon from 'components/AppIcon';

const LawyerMatching = () => {
  const [matchedLawyers, setMatchedLawyers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedLawyer, setSelectedLawyer] = useState(null);
  const [sortBy, setSortBy] = useState('rating');
  const [filterBy, setFilterBy] = useState('all');
  const [showProfileModal, setShowProfileModal] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  
  const formData = location?.state?.formData || {};

  useEffect(() => {
    // Simulate API call to get matched lawyers
    const fetchMatchedLawyers = async () => {
      setIsLoading(true);
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      const mockLawyers = [
        {
          id: 1,
          name: "Advocate Sarah Wanjiku",
          specialization: "Family Law",
          rating: 4.9,
          reviews: 127,
          experience: 12,
          location: "Nairobi",
          fees: "KES 15,000/hour",
          availability: "Available this week",
          image: "https://randomuser.me/api/portraits/women/42.jpg",
          bio: "Experienced family law attorney with focus on divorce, custody, and adoption cases. Fluent in English and Swahili.",
          credentials: ["LSK Member", "Family Law Specialist", "Mediator Certified"],
          languages: ["English", "Swahili", "Kikuyu"],
          responseTime: "Within 2 hours",
          successRate: 92,
          caseTypes: ["Divorce", "Child Custody", "Adoption", "Domestic Violence"],
          matchScore: 95,
          education: "LLB, University of Nairobi | LLM, Strathmore University",
          barNumber: "LSK/2012/4521",
          office: "Westlands, Nairobi"
        },
        {
          id: 2,
          name: "Advocate John Kamau",
          specialization: "Criminal Law",
          rating: 4.8,
          reviews: 89,
          experience: 15,
          location: "Nairobi",
          fees: "KES 20,000/hour",
          availability: "Available next week",
          image: "https://randomuser.me/api/portraits/men/35.jpg",
          bio: "Senior criminal defense attorney with extensive experience in white-collar crime and fraud cases.",
          credentials: ["LSK Member", "Criminal Law Expert", "Court of Appeal Advocate"],
          languages: ["English", "Swahili"],
          responseTime: "Within 4 hours",
          successRate: 87,
          caseTypes: ["Fraud", "Theft", "White Collar Crime", "Appeals"],
          matchScore: 88,
          education: "LLB, Kenyatta University | Diploma in Criminal Litigation",
          barNumber: "LSK/2009/3102",
          office: "CBD, Nairobi"
        },
        {
          id: 3,
          name: "Advocate Mary Akinyi",
          specialization: "Corporate Law",
          rating: 4.7,
          reviews: 156,
          experience: 10,
          location: "Mombasa",
          fees: "KES 18,000/hour",
          availability: "Available immediately",
          image: "https://randomuser.me/api/portraits/women/38.jpg",
          bio: "Corporate law specialist helping businesses with compliance, contracts, and regulatory matters.",
          credentials: ["LSK Member", "Corporate Law Certified", "Tax Law Specialist"],
          languages: ["English", "Swahili", "Luo"],
          responseTime: "Within 1 hour",
          successRate: 94,
          caseTypes: ["Business Formation", "Contracts", "Compliance", "Mergers"],
          matchScore: 91,
          education: "LLB, Moi University | LLM Corporate Law, University of Nairobi",
          barNumber: "LSK/2014/5890",
          office: "Nyali, Mombasa"
        },
        {
          id: 4,
          name: "Advocate Peter Kiprop",
          specialization: "Property Law",
          rating: 4.6,
          reviews: 73,
          experience: 8,
          location: "Nakuru",
          fees: "KES 12,000/hour",
          availability: "Available this week",
          image: "https://randomuser.me/api/portraits/men/41.jpg",
          bio: "Property law expert specializing in land disputes, real estate transactions, and property development.",
          credentials: ["LSK Member", "Property Law Specialist", "Land Tribunal Advocate"],
          languages: ["English", "Swahili", "Kalenjin"],
          responseTime: "Within 3 hours",
          successRate: 89,
          caseTypes: ["Land Disputes", "Property Sales", "Development", "Landlord-Tenant"],
          matchScore: 85,
          education: "LLB, Egerton University | Diploma in Land Law",
          barNumber: "LSK/2016/6234",
          office: "Nakuru Town"
        }
      ];

      setMatchedLawyers(mockLawyers);
      setIsLoading(false);
    };

    fetchMatchedLawyers();
  }, []);

  const filteredAndSortedLawyers = matchedLawyers?.filter(lawyer => {
      if (filterBy === 'all') return true;
      if (filterBy === 'immediate') return lawyer?.availability?.includes('immediately');
      if (filterBy === 'high-rated') return lawyer?.rating >= 4.8;
      if (filterBy === 'nearby') return lawyer?.location === formData?.county;
      return true;
    })?.sort((a, b) => {
      if (sortBy === 'rating') return b?.rating - a?.rating;
      if (sortBy === 'experience') return b?.experience - a?.experience;
      if (sortBy === 'match') return b?.matchScore - a?.matchScore;
      if (sortBy === 'fees') return parseInt(a?.fees?.replace(/[^\d]/g, '')) - parseInt(b?.fees?.replace(/[^\d]/g, ''));
      return 0;
    });

  const handleContactLawyer = (lawyer) => {
    navigate('/contact-lawyer', { state: { lawyer, formData } });
  };

  const handleViewProfile = (lawyer) => {
    setSelectedLawyer(lawyer);
    setShowProfileModal(true);
  };

  const handleCloseProfile = () => {
    setShowProfileModal(false);
    setSelectedLawyer(null);
  };

  const handleBackToSearch = () => {
    navigate('/client-registration');
  };

  // Lawyer Profile Modal
  const LawyerProfileModal = ({ lawyer, onClose, onContact }) => (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div 
        className="bg-surface rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
        onClick={(e) => e?.stopPropagation()}
      >
        {/* Modal Header */}
        <div className="flex items-center justify-between p-6 border-b border-border">
          <h2 className="text-xl font-heading font-bold text-text-primary">Lawyer Profile</h2>
          <button
            onClick={onClose}
            className="text-text-secondary hover:text-text-primary transition-smooth p-1 rounded-lg hover:bg-secondary-50"
          >
            <Icon name="X" size={20} />
          </button>
        </div>

        {/* Profile Content */}
        <div className="p-6">
          {/* Top Section */}
          <div className="flex items-start space-x-5 mb-6">
            <img
              src={lawyer?.image}
              alt={lawyer?.name}
              className="w-24 h-24 rounded-full object-cover border-4 border-primary-100"
            />
            <div className="flex-1">
              <h3 className="text-2xl font-heading font-bold text-text-primary mb-1">{lawyer?.name}</h3>
              <p className="text-primary font-semibold mb-2">{lawyer?.specialization}</p>
              <div className="flex items-center space-x-3 mb-2">
                <div className="flex items-center space-x-1">
                  <Icon name="Star" size={16} className="text-warning-500 fill-current" />
                  <span className="font-medium text-text-primary">{lawyer?.rating}</span>
                  <span className="text-text-secondary text-sm">({lawyer?.reviews} reviews)</span>
                </div>
                <span className="text-sm text-text-secondary">•</span>
                <span className="text-sm text-text-secondary">{lawyer?.experience} years experience</span>
              </div>
              <div className="flex items-center space-x-2 text-sm text-text-secondary">
                <Icon name="MapPin" size={14} />
                <span>{lawyer?.office || lawyer?.location}</span>
              </div>
            </div>
          </div>

          {/* Bar Number */}
          <div className="bg-primary-50 border border-primary-200 rounded-lg p-3 mb-6 flex items-center space-x-2">
            <Icon name="Shield" size={16} className="text-primary" />
            <span className="text-sm text-primary font-medium">LSK Bar Number: {lawyer?.barNumber}</span>
            <span className="ml-auto text-xs bg-success-100 text-success px-2 py-0.5 rounded-full font-medium">Verified</span>
          </div>

          {/* Bio */}
          <div className="mb-6">
            <h4 className="font-semibold text-text-primary mb-2">About</h4>
            <p className="text-text-secondary text-sm leading-relaxed">{lawyer?.bio}</p>
          </div>

          {/* Stats Grid */}
          <div className="grid grid-cols-3 gap-4 mb-6">
            <div className="bg-secondary-50 rounded-lg p-3 text-center">
              <p className="text-2xl font-bold text-primary">{lawyer?.successRate}%</p>
              <p className="text-xs text-text-secondary mt-1">Success Rate</p>
            </div>
            <div className="bg-secondary-50 rounded-lg p-3 text-center">
              <p className="text-2xl font-bold text-primary">{lawyer?.reviews}</p>
              <p className="text-xs text-text-secondary mt-1">Client Reviews</p>
            </div>
            <div className="bg-secondary-50 rounded-lg p-3 text-center">
              <p className="text-2xl font-bold text-primary">{lawyer?.matchScore}%</p>
              <p className="text-xs text-text-secondary mt-1">Match Score</p>
            </div>
          </div>

          {/* Education */}
          {lawyer?.education && (
            <div className="mb-6">
              <h4 className="font-semibold text-text-primary mb-2 flex items-center space-x-2">
                <Icon name="GraduationCap" size={16} className="text-primary" />
                <span>Education</span>
              </h4>
              <p className="text-text-secondary text-sm">{lawyer?.education}</p>
            </div>
          )}

          {/* Credentials */}
          <div className="mb-6">
            <h4 className="font-semibold text-text-primary mb-3 flex items-center space-x-2">
              <Icon name="Award" size={16} className="text-primary" />
              <span>Credentials & Certifications</span>
            </h4>
            <div className="space-y-2">
              {lawyer?.credentials?.map(credential => (
                <div key={credential} className="flex items-center space-x-2">
                  <Icon name="CheckCircle" size={14} className="text-success" />
                  <span className="text-sm text-text-secondary">{credential}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Case Types */}
          <div className="mb-6">
            <h4 className="font-semibold text-text-primary mb-3">Areas of Practice</h4>
            <div className="flex flex-wrap gap-2">
              {lawyer?.caseTypes?.map(type => (
                <span key={type} className="px-3 py-1 bg-primary-50 text-primary text-xs rounded-full border border-primary-200">
                  {type}
                </span>
              ))}
            </div>
          </div>

          {/* Languages */}
          <div className="mb-6">
            <h4 className="font-semibold text-text-primary mb-3">Languages</h4>
            <div className="flex flex-wrap gap-2">
              {lawyer?.languages?.map(lang => (
                <span key={lang} className="px-3 py-1 bg-secondary-100 text-secondary-700 text-xs rounded-full">
                  {lang}
                </span>
              ))}
            </div>
          </div>

          {/* Fees & Availability */}
          <div className="grid grid-cols-2 gap-4 mb-6">
            <div className="border border-border rounded-lg p-3">
              <p className="text-xs text-text-secondary mb-1">Consultation Fee</p>
              <p className="font-semibold text-text-primary">{lawyer?.fees}</p>
            </div>
            <div className="border border-border rounded-lg p-3">
              <p className="text-xs text-text-secondary mb-1">Response Time</p>
              <p className="font-semibold text-success">{lawyer?.responseTime}</p>
            </div>
          </div>

          {/* Availability Badge */}
          <div className="mb-6">
            <span className={`inline-flex items-center space-x-1.5 px-3 py-1.5 rounded-full text-sm font-medium ${
              lawyer?.availability?.includes('immediately') ? 'bg-success-100 text-success' :
              lawyer?.availability?.includes('this week') ? 'bg-warning-100 text-warning-700' : 'bg-secondary-100 text-text-secondary'
            }`}>
              <Icon name="Clock" size={14} />
              <span>{lawyer?.availability}</span>
            </span>
          </div>
        </div>

        {/* Modal Footer */}
        <div className="flex items-center space-x-3 p-6 border-t border-border bg-secondary-50 rounded-b-xl">
          <button
            onClick={onClose}
            className="flex-1 border border-border text-text-primary py-3 px-4 rounded-lg font-medium hover:bg-secondary-100 transition-smooth"
          >
            Close
          </button>
          <button
            onClick={() => { onClose(); onContact(lawyer); }}
            className="flex-1 bg-primary text-white py-3 px-4 rounded-lg font-medium hover:bg-primary-700 transition-smooth flex items-center justify-center space-x-2"
          >
            <Icon name="MessageCircle" size={16} />
            <span>Contact This Lawyer</span>
          </button>
        </div>
      </div>
    </div>
  );

  const LawyerCard = ({ lawyer }) => (
    <div className="bg-surface rounded-lg shadow-card border border-border p-6 hover:shadow-lg transition-smooth">
      <div className="flex items-start space-x-4">
        <img 
          src={lawyer?.image} 
          alt={lawyer?.name}
          className="w-16 h-16 rounded-full object-cover"
        />
        <div className="flex-1">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-xl font-heading font-semibold text-text-primary">
              {lawyer?.name}
            </h3>
            <div className="flex items-center space-x-1">
              <Icon name="Star" size={16} className="text-warning-500 fill-current" />
              <span className="text-sm font-medium text-text-primary">{lawyer?.rating}</span>
              <span className="text-sm text-text-secondary">({lawyer?.reviews} reviews)</span>
            </div>
          </div>
          
          <div className="flex items-center space-x-4 mb-3">
            <span className="text-sm text-primary font-medium">{lawyer?.specialization}</span>
            <span className="text-sm text-text-secondary">•</span>
            <span className="text-sm text-text-secondary">{lawyer?.experience} years exp.</span>
            <span className="text-sm text-text-secondary">•</span>
            <span className="text-sm text-text-secondary">{lawyer?.location}</span>
          </div>

          <p className="text-text-secondary text-sm mb-4 line-clamp-2">
            {lawyer?.bio}
          </p>

          <div className="grid grid-cols-2 gap-4 mb-4">
            <div>
              <span className="text-xs text-text-secondary block">Fees</span>
              <span className="text-sm font-medium text-text-primary">{lawyer?.fees}</span>
            </div>
            <div>
              <span className="text-xs text-text-secondary block">Response Time</span>
              <span className="text-sm font-medium text-text-primary">{lawyer?.responseTime}</span>
            </div>
            <div>
              <span className="text-xs text-text-secondary block">Success Rate</span>
              <span className="text-sm font-medium text-success">{lawyer?.successRate}%</span>
            </div>
            <div>
              <span className="text-xs text-text-secondary block">Match Score</span>
              <div className="flex items-center space-x-1">
                <div className="w-12 bg-secondary-100 rounded-full h-2">
                  <div 
                    className="bg-primary h-2 rounded-full"
                    style={{ width: `${lawyer?.matchScore}%` }}
                  ></div>
                </div>
                <span className="text-sm font-medium text-primary">{lawyer?.matchScore}%</span>
              </div>
            </div>
          </div>

          <div className="flex flex-wrap gap-2 mb-4">
            {lawyer?.languages?.slice(0, 3)?.map(lang => (
              <span key={lang} className="px-2 py-1 bg-secondary-100 text-secondary-700 text-xs rounded-full">
                {lang}
              </span>
            ))}
          </div>

          <div className="flex items-center justify-between">
            <span className={`text-sm font-medium ${
              lawyer?.availability?.includes('immediately') ? 'text-success' : 
              lawyer?.availability?.includes('this week') ? 'text-warning-600' : 'text-text-secondary'
            }`}>
              {lawyer?.availability}
            </span>
            <div className="flex items-center space-x-2">
              <button 
                onClick={() => handleViewProfile(lawyer)}
                className="flex items-center space-x-1 px-3 py-2 text-text-secondary hover:text-primary border border-border rounded-lg hover:bg-primary-50 hover:border-primary transition-smooth"
              >
                <Icon name="Eye" size={16} />
                <span>View Profile</span>
              </button>
              <button 
                onClick={() => handleContactLawyer(lawyer)}
                className="flex items-center space-x-1 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-700 transition-smooth"
              >
                <Icon name="MessageCircle" size={16} />
                <span>Contact</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <Icon name="Loader2" size={48} className="animate-spin text-primary mx-auto mb-4" />
          <h2 className="text-xl font-heading font-semibold text-text-primary mb-2">
            Finding Your Perfect Lawyer Match
          </h2>
          <p className="text-text-secondary">
            We're analyzing your legal needs and matching you with qualified lawyers...
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Lawyer Profile Modal */}
      {showProfileModal && selectedLawyer && (
        <LawyerProfileModal
          lawyer={selectedLawyer}
          onClose={handleCloseProfile}
          onContact={handleContactLawyer}
        />
      )}
      {/* Header */}
      <header className="bg-surface border-b border-border px-4 sm:px-6 lg:px-8 py-4">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="w-8 h-8 bg-primary rounded-lg flex items-center justify-center">
                <Icon name="Scale" size={20} color="white" />
              </div>
              <div>
                <span className="text-xl font-heading font-bold text-primary">LawDesk KE</span>
                <p className="text-sm text-text-secondary">Your Lawyer Matches</p>
              </div>
            </div>
            <button
              onClick={() => navigate('/')}
              className="text-text-secondary hover:text-text-primary transition-smooth"
            >
              <Icon name="X" size={20} />
            </button>
          </div>
        </div>
      </header>
      {/* Main Content */}
      <main className="px-4 sm:px-6 lg:px-8 py-8">
        <div className="max-w-7xl mx-auto">
          {/* Header Section */}
          <div className="mb-8">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h1 className="text-3xl font-heading font-bold text-text-primary mb-2">
                  Your Lawyer Matches
                </h1>
                <p className="text-text-secondary">
                  We found {matchedLawyers?.length} lawyers that match your needs{formData?.legalCategory ? ` for ${formData?.legalCategory}` : ''}
                </p>
              </div>
              <button
                onClick={handleBackToSearch}
                className="flex items-center space-x-2 px-4 py-2 text-text-secondary hover:text-text-primary border border-border rounded-lg hover:bg-secondary-50 transition-smooth"
              >
                <Icon name="ArrowLeft" size={16} />
                <span>Modify Search</span>
              </button>
            </div>

            {/* Your Request Summary */}
            {(formData?.legalCategory || formData?.county || formData?.urgency) && (
              <div className="bg-primary-50 border border-primary-200 rounded-lg p-4">
                <h3 className="font-medium text-primary mb-2">Your Legal Matter</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                  {formData?.legalCategory && (
                    <div>
                      <span className="text-text-secondary">Category:</span>
                      <span className="ml-2 font-medium text-text-primary">{formData?.legalCategory}</span>
                    </div>
                  )}
                  {formData?.county && (
                    <div>
                      <span className="text-text-secondary">Location:</span>
                      <span className="ml-2 font-medium text-text-primary">{formData?.county}</span>
                    </div>
                  )}
                  {formData?.urgency && (
                    <div>
                      <span className="text-text-secondary">Urgency:</span>
                      <span className="ml-2 font-medium text-text-primary">{formData?.urgency}</span>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Filters and Sort */}
          <div className="flex flex-col sm:flex-row gap-4 mb-6">
            <div className="flex items-center space-x-2">
              <Icon name="Filter" size={16} className="text-text-secondary" />
              <select 
                value={filterBy}
                onChange={(e) => setFilterBy(e?.target?.value)}
                className="border border-border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
              >
                <option value="all">All Lawyers</option>
                <option value="immediate">Available Immediately</option>
                <option value="high-rated">Highly Rated (4.8+)</option>
                <option value="nearby">In Your County</option>
              </select>
            </div>
            
            <div className="flex items-center space-x-2">
              <Icon name="ArrowUpDown" size={16} className="text-text-secondary" />
              <select 
                value={sortBy}
                onChange={(e) => setSortBy(e?.target?.value)}
                className="border border-border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
              >
                <option value="match">Best Match</option>
                <option value="rating">Highest Rated</option>
                <option value="experience">Most Experience</option>
                <option value="fees">Lowest Fees</option>
              </select>
            </div>
          </div>

          {/* Lawyers List */}
          <div className="space-y-6">
            {filteredAndSortedLawyers?.map(lawyer => (
              <LawyerCard key={lawyer?.id} lawyer={lawyer} />
            ))}
          </div>

          {/* No Results */}
          {filteredAndSortedLawyers?.length === 0 && (
            <div className="text-center py-12">
              <Icon name="Search" size={48} className="text-text-secondary mx-auto mb-4" />
              <h3 className="text-xl font-heading font-semibold text-text-primary mb-2">
                No lawyers match your current filters
              </h3>
              <p className="text-text-secondary mb-4">
                Try adjusting your filters or search criteria
              </p>
              <button
                onClick={() => setFilterBy('all')}
                className="bg-primary text-white px-6 py-2 rounded-lg hover:bg-primary-700 transition-smooth"
              >
                Show All Lawyers
              </button>
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default LawyerMatching;